import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion"; // <-- Add AnimatePresence here
import { HiServer, HiKey, HiDatabase } from "react-icons/hi";
import {
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Box,
  Divider,
  Button,
  createTheme,
  ThemeProvider,
  useMediaQuery,
  Container,
  Link as MuiLink,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";


import Login from "./Logincontainer";
import ApiKeys from "./ApiKeys";
import Containers from "./Containers";
import Images from "./Images";

const drawerWidth = 240;

function App() {
  const [token, setToken] = useState(localStorage.getItem("jwt") || "");
  const [activePage, setActivePage] = useState("containers");

  // Theme toggle state
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)", { noSsr: true });
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark" || prefersDarkMode
  );

  const logout = () => {
    setToken("");
    localStorage.removeItem("jwt");
  };

  if (!token) return <Login setToken={setToken} />;

  const menuItems = [
    { text: "Containers", key: "containers", icon: <HiServer /> },
    { text: "Images", key: "images", icon: <HiDatabase /> },
    { text: "API Keys", key: "apikeys", icon: <HiKey /> },
  ];

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: { main: "#6366f1" },
          background: {
            default: darkMode ? "#0f172a" : "#f9fafb",
            paper: darkMode ? "rgba(30, 41, 59, 0.7)" : "rgba(255, 255, 255, 0.7)",
          },
        },
        typography: {
          fontFamily: "'Inter', 'Roboto', sans-serif",
        },
        shape: { borderRadius: 12 },
      }),
    [darkMode]
  );

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    localStorage.setItem("theme", !darkMode ? "dark" : "light");
  };

  const renderContent = () => {
    switch (activePage) {
      case "containers":
        return <Containers token={token} />;
      case "images":
        return <Images token={token} />;
      case "apikeys":
        return <ApiKeys token={token} />;
      default:
        return <Typography>Welcome to the Dashboard</Typography>;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh", position: "relative", overflow: "hidden" }}>
        {/* Animated Background Gradient */}
        <motion.div
          initial={{ backgroundPosition: "0% 50%" }}
          animate={{ backgroundPosition: "100% 50%" }}
          transition={{ duration: 15, repeat: Infinity, repeatType: "reverse" }}
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(270deg, #6366f1, #3b82f6, #06b6d4, #9333ea)",
            backgroundSize: "600% 600%",
            zIndex: -1,
            opacity: 0.15,
          }}
        />

        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              backdropFilter: "blur(12px)",
              backgroundColor: theme.palette.background.paper,
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          <Toolbar>
            <Typography variant="h6" noWrap fontWeight="600">
              🚀 Dashboard
            </Typography>
          </Toolbar>
          <Divider />
          <List>
            {menuItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ x: -30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.15 }}
              >
                <ListItem disablePadding>
                  <ListItemButton
                    selected={activePage === item.key}
                    onClick={() => setActivePage(item.key)}
                  >
                    <ListItemIcon sx={{ color: "text.primary" }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              </motion.div>
            ))}
          </List>
          <Box sx={{ flexGrow: 1 }} />
          <Box p={2}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={logout}
            >
              Logout
            </Button>
          </Box>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.default",
          }}
        >
          {/* Top Navbar */}
          <AppBar
            position="static"
            elevation={0}
            sx={{
              backdropFilter: "blur(12px)",
              bgcolor: theme.palette.background.paper,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" fontWeight="600" color="primary">
                Admin Panel
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <IconButton onClick={toggleTheme} color="inherit">
                  {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                  Welcome, Admin
                </Typography>
                <Avatar src="https://api.dicebear.com/7.x/identicon/svg?seed=Admin" />
              </Box>
            </Toolbar>
          </AppBar>

          {/* Page Content */}
          <Box component="div" flexGrow={1} p={3}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activePage}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* Footer */}
          <Box
            component="footer"
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              mt: "auto",
              py: 2,
              backdropFilter: "blur(12px)",
              bgcolor: theme.palette.background.paper,
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                © {new Date().getFullYear()} HarishaCrypto Inc. All rights reserved.
              </Typography>
              <Box display="flex" gap={3}>
                <MuiLink href="#" underline="hover" color="primary">
                  Docs
                </MuiLink>
                <MuiLink href="#" underline="hover" color="primary">
                  Support
                </MuiLink>
                <MuiLink href="#" underline="hover" color="primary">
                  Terms
                </MuiLink>
              </Box>
            </Container>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
